import { getEnv } from '@risksmart-app/components/src/utils/environment';
import type { Feature } from '@risksmart-app/shared/organisation/Feature';
import { useCallback } from 'react';
import { useModules } from 'src/context/moduleContext';
import { useFeatures } from 'src/rbac/useFeatures';

export type IsFeatureVisibleToOrg = (feature: Feature) => boolean | null;

type FeatureConfig =
  | {
      moduleKey: string;
      isEnabledBeforeModules: boolean;
      type: 'module';
    }
  | {
      isEnabledBeforeModules: boolean;
      type: 'feature-flag';
    };

// Map of feature flags to either module keys or feature-flag types.
const featureMap: Record<Feature, FeatureConfig> = {
  day_attestations: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  acceptance: {
    moduleKey: 'risk.subModules.acceptance',
    isEnabledBeforeModules: true,
    type: 'module',
  },
  action: {
    moduleKey: 'action',
    isEnabledBeforeModules: true,
    type: 'module',
  },
  appetite: {
    moduleKey: 'risk.subModules.appetite',
    isEnabledBeforeModules: true,
    type: 'module',
  },
  appetite_links: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  approvers: {
    moduleKey: 'approval',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  assessment: {
    moduleKey: 'assessment',
    isEnabledBeforeModules: true,
    type: 'module',
  },
  attestations: {
    moduleKey: 'document.subModules.attestation',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  authentication: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  bedrock: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  chat: {
    moduleKey: 'ai.subModules.chat',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  chat_warning: {
    moduleKey: 'ai.subModules.chat_warning',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  compliance: {
    moduleKey: 'obligation',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  compliance_monitoring: {
    moduleKey: 'obligation.subModules.compliance_monitoring_assessment',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  control: {
    moduleKey: 'control',
    isEnabledBeforeModules: true,
    type: 'module',
  },
  control_group: {
    moduleKey: 'control.subModules.control_group',
    isEnabledBeforeModules: true,
    type: 'module',
  },
  pdf_export: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  'disable-causes': {
    moduleKey: 'issue.subModules.cause',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  'disable-consequences': {
    moduleKey: 'issue.subModules.consequence',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  easter_eggs: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  enterprise_risk: {
    moduleKey: 'enterprise_risk',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  guidance: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  impacts: {
    moduleKey: 'risk.subModules.impact',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  incident_reporting: {
    moduleKey: 'incident_reporting',
    isEnabledBeforeModules: true,
    type: 'module',
  },
  indicator: {
    moduleKey: 'indicator',
    isEnabledBeforeModules: true,
    type: 'module',
  },
  internal_audit: {
    moduleKey: 'internal_audit_entity',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  issue: {
    moduleKey: 'issue',
    isEnabledBeforeModules: true,
    type: 'module',
  },
  'issue-allica': {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  'issue-gc': {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  modules: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  multi_reporting: {
    moduleKey: 'custom_datasource',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  notifications: {
    moduleKey: 'notification',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  policy: {
    moduleKey: 'document',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  policy_auto_draft: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  posture: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  risk: {
    moduleKey: 'risk',
    isEnabledBeforeModules: true,
    type: 'module',
  },
  third_party: {
    moduleKey: 'third_party',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  tpp_contacts: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  trpc: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  'turn-off-regulatory-breaches': {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  wizard: {
    moduleKey: 'risk.subModules.rcsa_wizard',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  empty_default_dashboard: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  conditional_fields: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  custom_attribute_alternate_values: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  alt_values: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  attestation_improvements: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  ai_suggest_controls: {
    moduleKey: 'ai.subModules.suggested_controls',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  no_inherit: {
    type: 'feature-flag',
    isEnabledBeforeModules: false,
  },
  scoring_settings: {
    moduleKey: 'risk.subModules.risk_scoring',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  scoring_settings_data: {
    isEnabledBeforeModules: false,
    type: 'feature-flag',
  },
  reg_feed: {
    moduleKey: 'obligation.subModules.reg_feed',
    isEnabledBeforeModules: false,
    type: 'module',
  },
  integrations: {
    moduleKey: 'integrations',
    isEnabledBeforeModules: false,
    type: 'module',
  },
};

export const useIsFeatureVisibleToOrgLazy = (): ((
  feature: Feature
) => boolean) => {
  const features = useFeatures();
  const { isModuleEnabled } = useModules();

  const modulesEnabled = features.includes('modules');

  return useCallback(
    (feature: Feature) => {
      if (!modulesEnabled) {
        // Handle feature flags that have been added for modules
        // but need to be available before modules are enabled.
        if (featureMap[feature].isEnabledBeforeModules) {
          return true;
        }

        return (
          features.includes(feature) ||
          isFeatureEnabled(`REACT_APP_FEATURE_${feature}`)
        );
      }

      // Get the feature map entry for the current feature.
      const featureMapEntry = featureMap[feature];

      // Handle feature-flag type features.
      if (featureMapEntry.type === 'feature-flag') {
        // Modules are enabled, however these are feature flags that are not tied to modules.
        // Allow env vars and features array to drive these until features are reworked.
        return (
          features.includes(feature) ||
          isFeatureEnabled(`REACT_APP_FEATURE_${feature}`)
        );
      }

      // Modules are enabled, only use module based keys.
      return isModuleEnabled(featureMapEntry.moduleKey);
    },
    [features, isModuleEnabled, modulesEnabled]
  );
};

export const useIsFeatureVisibleToOrg = (feature: Feature): boolean => {
  const isFeatureVisibleToOrg = useIsFeatureVisibleToOrgLazy();

  return isFeatureVisibleToOrg(feature);
};

export type FeatureFlagKey = `REACT_APP_FEATURE_${Feature}`;

export const isFeatureEnabled = (flag: FeatureFlagKey) => {
  return getEnv(flag, true) === 'true';
};
